/**
 * Redis Stream entry ids (`ms-n`) used as the global resume cursor (D-075). Stream ids are
 * unique within one stream but two streams can issue ids in the same millisecond, so replay
 * restarts 1 s before the cursor (`replayStartId`) and clients de-duplicate by entity version.
 */
const EID = /^(\d{1,16})-(\d{1,16})$/;

export function isEid(value: unknown): value is string {
  return typeof value === 'string' && EID.test(value);
}

export function parseEid(eid: string): { ms: number; seq: number } {
  const match = EID.exec(eid);
  if (!match) throw new Error(`Invalid stream id: ${eid}`);
  return { ms: Number(match[1]), seq: Number(match[2]) };
}

/** Milliseconds part of a stream id. */
export function eidMs(eid: string): number {
  return parseEid(eid).ms;
}

/** Negative, zero or positive like a sort comparator. */
export function compareEid(a: string, b: string): number {
  const x = parseEid(a);
  const y = parseEid(b);
  return x.ms !== y.ms ? x.ms - y.ms : x.seq - y.seq;
}

/** The larger of two cursors (either may be null). */
export function maxEid(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return compareEid(a, b) >= 0 ? a : b;
}

/** `XRANGE` start for a resume: 1 s before the cursor's millisecond (PLAN §5 "Replay start"). */
export function replayStartId(cursor: string, overlapMs = 1000): string {
  return `${Math.max(0, eidMs(cursor) - overlapMs)}-0`;
}

/** A cursor for "now" from a millisecond timestamp (the node watermark is `t0 − 1000`). */
export function eidFromMs(ms: number): string {
  return `${Math.max(0, Math.floor(ms))}-0`;
}
